import React from 'react';
import SEOHead, { faqSchema } from '@components/marketing/SEOHead';
import PageHead from '@components/marketing/PageHead';
import CtaBand from '@components/marketing/CtaBand';

// A season really is a sequence, which is the one case where numbering a list
// carries information rather than decorating it.
const STAGES = [
  {
    name: 'Registration',
    body: 'Entries arrive through a form on your own site or by scanning a code on a poster, and land with the city or unit they belong to. A coordinator checks each one before it joins anything — eligibility, category, duplicates — so the draw is built from a list somebody has actually looked at.',
    detail: ['Enter one player at a time, or a whole team at once', 'A different registration code per city, so entries arrive pre-sorted', 'Approval queue with a running count of what is still pending'],
  },
  {
    name: 'The draw',
    body: 'Set the format once — group stage, straight knockout, or groups into a knockout — and the draw is generated for a whole sport at a time. Uneven entries get byes distributed through the early rounds rather than bunched, so no one plays three matches while someone else plays one.',
    detail: ['Groups, knockouts, or groups feeding a knockout', 'Byes spread progressively across the first rounds', 'Separate draws for men’s, women’s and mixed categories'],
  },
  {
    name: 'The schedule',
    body: 'Fixtures are placed against real venues, courts and slots. Anyone entered in two sports is never asked to be in two places at once, and a match moved late drags the rounds that depend on it along with it.',
    detail: ['Clash detection across sports for the same player', 'Slot lengths and court counts per venue', 'Late entries absorbed without redrawing what has already been played'],
  },
  {
    name: 'Scoring',
    body: 'Scorers work on a phone at the court, each limited to one sport in one city and signed in on one device at a time. Every sport is scored on its own terms, and the score reaches the boards while the match is still being played.',
    detail: ['Per-sport scoring, not one generic scoreboard', 'Undo for the point that should not have counted', 'Check-in at the court before the match starts'],
  },
  {
    name: 'Standings',
    body: 'Group tables build themselves as results arrive — three points a win, one a draw — for every city and every category. Record boards follow from the same data: most runs, most wickets, most sixes, top scorers.',
    detail: ['League tables with played, won, drawn, lost, for, against and difference', 'Knockout progression showing how far each entry got', 'Record boards by sport and by city'],
  },
  {
    name: 'Certificates',
    body: 'Once a tournament’s results are in, participation and winners’ certificates are generated for everyone who took part, on your own template, and can be downloaded in bulk or verified individually later.',
    detail: ['Your template, your signatures', 'Participation and placing certificates', 'A verification page so a certificate can be checked against the record'],
  },
];

const FAQS = [
  ['What is tournament management software?',
   'It is the system an association uses to run a competition end to end: taking registrations, generating the draw, scheduling fixtures against venues, recording scores, producing standings and issuing certificates. SportsTMS does all of those in one place rather than spreading them across spreadsheets, message groups and paper scoresheets.'],
  ['Can it run more than one sport at the same time?',
   'Yes. A season can cover several sports running in parallel across different venues, each with its own format, categories and scoring rules. A player entered in two sports will not be scheduled into both at once.'],
  ['Do scorers need training?',
   'The scoring screen shows the rules of the sport it is scoring and refuses entries the sport cannot produce, which removes most of what training would otherwise have to cover. One session per role is included, and it is recorded.'],
  ['Does each association get its own website?',
   'Yes. Every association runs on its own address with its own logo, colours and content, and its own data. Nothing is shared between one association and another.'],
  ['Does it work offline at the venue?',
   'Scoring needs a connection to publish to the boards. Where a venue has poor coverage, scorers usually work from a phone’s mobile data; a score entered is sent as soon as the connection returns.'],
];

const PlatformPage = () => (
  <>
    <SEOHead
      title="What the platform covers"
      description="How SportsTMS runs a season stage by stage: registration and approvals, draw generation, scheduling with clash detection, live scoring, standings and certificates."
      schema={faqSchema(FAQS)}
    />

    <PageHead title="A season, stage by stage" image="/images/sportstms/arena.jpg">
          <p>
            Every competition runs the same six stages, whether it is one sport in one hall or
            eight across a dozen venues. SportsTMS covers all six, so nothing has to be carried
            between systems by hand.
          </p>
    </PageHead>

    <section className="stms-sec">
      <div className="stms-wrap">
        <ol className="stms-stages">
          {STAGES.map((s, i) => (
            <li key={s.name}>
              <div className="stms-stages__mark" aria-hidden="true">
                <span className="stms-num">{i + 1}</span>
              </div>
              <div className="stms-stages__body">
                <h2>{s.name}</h2>
                <p>{s.body}</p>
              </div>
              <ul className="stms-stages__has">
                {s.detail.map((d) => <li key={d}>{d}</li>)}
              </ul>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section className="stms-sec stms-sec--paper">
      <div className="stms-wrap">
        <div className="stms-lede"><h2>Common questions</h2></div>
        <dl className="stms-faq">
          {FAQS.map(([q, a]) => (
            <div key={q}>
              <dt>{q}</dt>
              <dd>{a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>

    <CtaBand title="See it with your own season in it" image="/images/sportstms/colleagues.jpg" action="Book a demo">
      <p>We set up a working tenant using your sports, venues and categories before the call.</p>
    </CtaBand>
  </>
);

export default PlatformPage;
